/**
 * @module features/devices/components/DeviceTableSkeleton
 * @description Geometry-matched loading placeholders. Row/card heights equal the
 * real components (52px rows, fixed-height cards) so loading never shifts layout (CLS = 0).
 */
import * as React from "react";
import { Skeleton } from "@/components/ui/skeleton";

export const DEVICE_ROW_HEIGHT = "h-[52px]";

export function DeviceTableSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <div
      className="hidden sm:block rounded-xl border border-border/80 bg-card overflow-hidden shadow-xs"
      aria-busy="true"
      aria-label="Loading devices"
    >
      <div className="h-10 border-b border-border/80 bg-muted/30" />
      <div className="divide-y divide-border/60">
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className={`${DEVICE_ROW_HEIGHT} flex items-center gap-4 px-4`}>
            <div className="flex-1 min-w-0 space-y-1.5">
              <Skeleton className="h-3.5 w-36" />
              <Skeleton className="h-3 w-28" />
            </div>
            <Skeleton className="h-3.5 w-36 hidden md:block" />
            <Skeleton className="h-3.5 w-24 hidden lg:block" />
            <Skeleton className="h-5 w-24 rounded-full" />
            <Skeleton className="h-8 w-16 rounded-md" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function DeviceCardSkeleton({ cards = 5 }: { cards?: number }) {
  return (
    <div className="sm:hidden space-y-2.5" aria-busy="true" aria-label="Loading devices">
      {Array.from({ length: cards }, (_, i) => (
        <div
          key={i}
          className="h-[112px] rounded-xl border border-border/80 bg-card p-3 shadow-xs space-y-3"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1.5">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-40" />
            </div>
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
          <Skeleton className="h-3 w-24" />
        </div>
      ))}
    </div>
  );
}
